import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Box,
  Typography,
  TextField,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MuiAlert from '@mui/material/Alert';
import axios from 'axios';
import { API_URL } from '../../common/Constants';
import Cookies from '../../common/Cookies';

const Alert = MuiAlert;

const StatusIoModal = ({ open, onClose }) => {
  const [statusPageUrl, setStatusPageUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);

  const authHeader = () => {
    const jwtToken = Cookies.getJwtToken();
    if (jwtToken) return { Authorization: `Bearer ${jwtToken}` };
    const tenantId = Cookies.getTenantId();
    return tenantId ? { Authorization: String(tenantId) } : {};
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API_URL}/integrations/statusio`, { headers: authHeader() });
        setStatusPageUrl(res.data?.statusPageUrl || '');
      } catch (e) {
        setStatusPageUrl('');
        setError('Failed to load Status.io settings.');
      } finally {
        setLoading(false);
      }
    };
    if (open) {
      load();
      setSnackbar({ open: false, message: '', severity: 'success' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSave = async () => {
    const trimmed = (statusPageUrl || '').trim();
    if (trimmed && !trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
      setSnackbar({ open: true, message: 'URL must start with http:// or https://', severity: 'error' });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/integrations/statusio`, { statusPageUrl: trimmed }, { headers: authHeader() });
      setSnackbar({ open: true, message: 'Settings saved.', severity: 'success' });
      setTimeout(() => onClose && onClose(), 1000);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to save settings.', severity: 'error' });
      setError('Failed to save Status.io settings.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: 99999 }}>
        <DialogTitle sx={{ position: 'relative', pr: 6 }}>
          Status.io
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8, color: '#888', background: '#f5f5f5' }}
            aria-label="close"
          >
            <CloseIcon fontSize="medium" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loading && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={32} />
            </Box>
          )}
          {!loading && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Paste your Status.io <strong>public status JSON API</strong> URL (read-only), for example{' '}
                <code style={{ fontSize: '0.85em' }}>https://…hostedstatus.com/1.0/status/…</code>. This URL is stored per
                tenant and used when agents call the status tool.
              </Typography>
              <TextField
                label="Public status API URL"
                name="statusPageUrl"
                value={statusPageUrl}
                onChange={(e) => setStatusPageUrl(e.target.value)}
                fullWidth
                margin="normal"
                placeholder="https://…"
                helperText="Leave empty and save to disconnect."
              />
              {error && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                  {error}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default StatusIoModal;
